/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Mon Apr 08 2024
 **/

import { useOutsideClickEffect } from "@/frameworks/libraries/hooks";
import { Column, TextField } from "@/frameworks/designStuff/components";
import { Maybe } from "@/frameworks/libraries/components";

import { useFileRename } from "../../hooks";
import * as S from "../emotion";

export default function FileName({
	file,
	isShare3D
}: {
	file: Required<FileBox.Folders.GetFolderFileListResponse>["content"][number];
	isShare3D?: boolean;
}) {
	const { isUpdateStarted, fileName, fileNameForUpdate, updateFileName, updateFolderName, handleChange } =
		useFileRename(file);

	const updateName = () => {
		if (file.fileBoxType === "FILE") {
			return updateFileName();
		}
		if (file.fileBoxType === "FOLDER") {
			return updateFolderName();
		}
	};

	const { ref } = useOutsideClickEffect(() => {
		updateName();
	}, [file.fileBoxType, updateName]);

	return (
		<Column css={{ width: "100%" }} ref={ref}>
			<Maybe
				test={!isUpdateStarted}
				fallback={
					<TextField
						autoFocus
						size={32}
						maxLength={50}
						value={fileNameForUpdate}
						placeholder={fileName}
						onChange={handleChange}
						onKeyDown={(ev) => {
							if (ev.key === "Enter") {
								if (file.fileBoxType === "FILE") {
									updateFileName();
								}
								if (file.fileBoxType === "FOLDER") {
									updateFolderName();
								}
							}
						}}
					/>
				}
			>
				<S.FileName
					maxLine={1}
					ellipsis
					align="left"
					typoSize="Title2"
					typoColor={status === "in-use" || isShare3D ? "neutral_00_100" : "neutral_80_100"}
				>
					{fileName}
				</S.FileName>
			</Maybe>
		</Column>
	);
}
